import * as KeyCode from "keycode";
import * as Topics from "../Data/Topics";
import {IFocusable} from "./IFocusable";
import * as InputUtils from "./InputUtils";
import {Direction, KeyboardMap} from "./KeyboardMap";
import {KeyboardMapToken} from "./KeyboardMapToken";
import {KeyCodes} from "./KeyCodes";

export interface IKeyboardStatus {
    enabled: boolean;
    active: boolean;
    layer: number;
    currentFocusable: IFocusable | undefined;
}

/**
 * Keyboard manager
 */
export class KeyboardManager {
    private _maps: KeyboardMap[];
    private _isActive: boolean;
    private _isEnabled: boolean;

    constructor() {
        this._isActive = false;
        this._isEnabled = true;

        this._maps = new Array<KeyboardMap>();

        window.addEventListener(InputUtils.Events.KEY_DOWN, this.onKeyDown.bind(this), false);
        window.addEventListener(InputUtils.Events.MOUSE_DOWN, this.onMouseDown.bind(this), false);
        window.addEventListener(InputUtils.Events.FOCUS, this.onBrowserFocus.bind(this));
        window.addEventListener(InputUtils.Events.BLUR, this.onBrowserBlur.bind(this));
        PubSub.subscribe(Topics.REGISTER_FOCUSABLE, this.onRegisterFocusable.bind(this));
        PubSub.subscribe(Topics.REGISTER_FOCUSABLES, this.onRegisterFocusable.bind(this));
        PubSub.subscribe(Topics.UNREGISTER_FOCUSABLE, this.onUnregisterFocusable.bind(this));
        PubSub.subscribe(Topics.UNREGISTER_FOCUSABLES, this.onUnregisterFocusable.bind(this));
        PubSub.subscribe(Topics.UNREGISTER_ALL_FOCUSABLES, this.onUnregisterAllFocusables.bind(this));
        PubSub.subscribe(Topics.CLEAR_FOCUS, this.onClearFocus.bind(this));
        PubSub.subscribe(Topics.FORCE_FOCUS, this.onForceFocus.bind(this));
        PubSub.subscribe(Topics.KEYBOARD_FORCE_NEIGHBOURS, this.onForceNeighbours.bind(this));
        PubSub.subscribe(Topics.KEYBOARD_CLEAR_NEIGHBOURS, this.onClearNeighbours.bind(this));
        PubSub.subscribe(Topics.PUSH_KEYBOARD_LAYER, this.pushMapLayer.bind(this));
        PubSub.subscribe(Topics.POP_KEYBOARD_LAYER, this.popMapLayer.bind(this));
        PubSub.subscribe(Topics.SET_KEYBOARD_ENABLED, this.onSetKeyboardEnabled.bind(this));
        PubSub.subscribe(Topics.GET_KEYBOARD_STATUS, this.onGetKeyboardStatus.bind(this));
    }

    /**
     * onKeyDown
     * @param pEvent
     */
    private onKeyDown(pEvent: KeyboardEvent): void {
        if (!this._isEnabled) {
            return;
        }
        if (this._isActive === false) {
            if (this._maps.length > 0) {
                this._isActive = true;
                this._maps[0].isActive = true;
                pEvent.preventDefault();
            }
        } else {
            if (KeyCode.isEventKey(pEvent, KeyCodes.UP) || KeyCode.isEventKey(pEvent, KeyCodes.W)) {
                this.onDirectionPressed(Direction.UP);
                pEvent.preventDefault();
            } else if (KeyCode.isEventKey(pEvent, KeyCodes.RIGHT) || KeyCode.isEventKey(pEvent, KeyCodes.D)) {
                this.onDirectionPressed(Direction.RIGHT);
                pEvent.preventDefault();
            } else if (KeyCode.isEventKey(pEvent, KeyCodes.DOWN) || KeyCode.isEventKey(pEvent, KeyCodes.S)) {
                this.onDirectionPressed(Direction.DOWN);
                pEvent.preventDefault();
            } else if (KeyCode.isEventKey(pEvent, KeyCodes.LEFT) || KeyCode.isEventKey(pEvent, KeyCodes.A)) {
                this.onDirectionPressed(Direction.LEFT);
                pEvent.preventDefault();
            } else if (KeyCode.isEventKey(pEvent, KeyCodes.ENTER) || KeyCode.isEventKey(pEvent, KeyCodes.SPACEBAR)) {
                if (this._maps.length > 0) {
                    this._maps[0].activateFocussedNode();
                    pEvent.preventDefault();
                }
            }
        }
    }

    /**
     * onDirectionPressed
     * @param pDirection
     */
    private onDirectionPressed(pDirection: Direction): void {
        if (this._maps.length > 0) {
            this._maps[0].step(pDirection);
        }
    }

    /**
     * onMouseDown
     */
    private onMouseDown(): void {
        if (this._isActive === true) {
            this._isActive = false;
            if (this._maps.length > 0) {
                this._maps[0].isActive = false;
            }
        }
    }

    /**
     * onBrowserBlur
     */
    private onBrowserBlur(): void {
        if (this._isActive && this._maps.length > 0) {
            this._maps[0].isActive = false;
        }
    }

    /**
     * onBrowserFocus
     */
    private onBrowserFocus(): void {
        if (this._isActive && this._maps.length > 0) {
            this._maps[0].isActive = true;
        }
    }

    private onRegisterFocusable(pTopic: string, pData: IFocusable | IFocusable[]) {
        if (this._maps.length > 0) {
            this._maps[0].registerFocusable(pData);
        }
    }

    private onUnregisterFocusable(pTopic: string, pData: (IFocusable | ((it: IFocusable) => boolean)) | Array<IFocusable | ((it: IFocusable) => boolean)>) {
        for (const map of this._maps) {
            map.unregisterFocusable(pData);
        }
    }

    private onUnregisterAllFocusables() {
        for (let i = 0; i < this._maps.length; ++i) {
            this._maps[i].clear();
        }
    }

    private onClearFocus() {
        if (this._maps.length > 0) {
            this._maps[0].clearFocus();
        }
    }

    private onForceFocus(pTopic: string, pData: IFocusable) {
        if (this._isActive && this._maps.length > 0) {
            this._maps[0].setFocus(pData);
        }
    }

    private onForceNeighbours(pTopic: string, pData: KeyboardMapToken | KeyboardMapToken[]) {
        if (this._maps.length > 0) {
            if (!Array.isArray(pData)) {
                pData = [pData];
            }
            for (const token of pData) {
                for (let i = 0; i < Direction.NUM_ELEMENTS; ++i) {
                    if (token.neighbours[i] !== undefined) {
                        this._maps[0].forceNeighbour(token.target, token.neighbours[i]!, i);
                    }
                }
            }
        }
    }

    private onClearNeighbours() {
        if (this._maps.length > 0) {
            this._maps[0].clearNeighbours();
        }
    }

    private onSetKeyboardEnabled(pTopic: string, pData: boolean) {
        this._isEnabled = pData;
    }

    private onGetKeyboardStatus(pTopic: string, pData: (status: IKeyboardStatus) => void) {
        pData({
            enabled: this._isEnabled,
            active: this._isActive,
            layer: this._maps.length,
            currentFocusable: this._maps.length > 0 ? this._maps[0].currentFocusable : undefined,
        });
    }

    /**
     * Pushs map layer
     */
    private pushMapLayer(): void {
        if (this._maps.length > 0) {
            this._maps[0].isActive = false;
        }
        const map = new KeyboardMap();
        this._maps.unshift(map);
        map.isActive = this._isActive;
    }

    /**
     * Pops map layer
     */
    private popMapLayer(): void {
        if (this._maps.length > 0) {
            this._maps[0].isActive = false;
            this._maps[0].clear();
        }
        this._maps.shift();
        if (this._maps.length > 0) {
            this._maps[0].isActive = this._isActive;
        }
    }
}
